import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, IconButton, Tooltip,
  TablePagination, Alert, MenuItem
} from '@mui/material';
import { Search, Refresh, AccountBalance, AddCircle, RemoveCircle, ReceiptLong } from '@mui/icons-material';
import api from '../../services/api';
import { useAuthStore } from '../../store/auth';

export const BillingPage: React.FC = () => {
  const { user } = useAuthStore();
  const canWrite = user?.role !== 'executive';
  const [txns, setTxns] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [topupDialog, setTopupDialog] = useState(false);
  const [tenants, setTenants] = useState<any[]>([]);
  const [topupForm, setTopupForm] = useState({ tenant_id: '', amount: '', type: 'credit', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [billNowDialog, setBillNowDialog] = useState(false);
  const [billNowForm, setBillNowForm] = useState({ tenant_id: '', due_days: '7' });
  const [billNowResult, setBillNowResult] = useState<any>(null);
  const [billNowSaving, setBillNowSaving] = useState(false);
  const [billNowErr, setBillNowErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '50', offset: String(page * 50) });
      if (search) p.set('search', search);
      const { data: res } = await api.get(`/billing/transactions?${p}`).catch(() => api.get('/billing/balance'));
      const rows = res.data || (Array.isArray(res) ? res : [res]);
      setTxns(rows);
      setTotal(res.total || rows.length);
    } catch (e) { console.error(e); setTxns([]); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/tenants?limit=200&tier=customer').then(({ data: r }) => setTenants(r.data || [])).catch(() => {});
  }, []);

  const doTopup = async () => {
    setSaving(true); setError('');
    try {
      const amount = topupForm.type === 'debit' ? -Math.abs(parseFloat(topupForm.amount)) : Math.abs(parseFloat(topupForm.amount));
      await api.post(`/billing/topup/${topupForm.tenant_id}`, { amount, notes: topupForm.notes });
      setTopupDialog(false);
      load();
    } catch (e: any) { setError(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const doBillNow = async () => {
    setBillNowSaving(true); setBillNowErr(''); setBillNowResult(null);
    try {
      const { data } = await api.post(`/invoices/bill-now/${billNowForm.tenant_id}`, { due_days: parseInt(billNowForm.due_days) || 7 });
      setBillNowResult(data);
    } catch (e: any) { setBillNowErr(e.response?.data?.message || 'Failed to create invoice'); }
    finally { setBillNowSaving(false); }
  };

  const TYPE_COLOR: any = { credit: 'success', debit: 'error', refund: 'info', adjustment: 'warning' };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AccountBalance sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Billing & Balance</Typography>
            <Typography variant="body2" color="text.secondary">Transaction ledger across all customers</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" placeholder="Search..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 220 }} />
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          {canWrite && <Button variant="outlined" startIcon={<ReceiptLong />}
            onClick={() => { setBillNowForm({ tenant_id: '', due_days: '7' }); setBillNowErr(''); setBillNowResult(null); setBillNowDialog(true); }}>
            Bill Now
          </Button>}
          {canWrite && <Button variant="contained" startIcon={<AddCircle />} color="success"
            onClick={() => { setTopupForm({ tenant_id: '', amount: '', type: 'credit', notes: '' }); setError(''); setTopupDialog(true); }}>
            Add Balance
          </Button>}
        </Box>
      </Box>

      <Card>
        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Customer', 'Type', 'Amount', 'Notes', 'Date'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {txns.map((row, i) => (
                  <TableRow key={row.id || i} hover>
                    <TableCell>{tenants.find(t => t.id === row.tenant_id)?.name || row.tenant_id || '—'}</TableCell>
                    <TableCell><Chip label={row.type || row.transaction_type || 'transaction'} size="small" color={TYPE_COLOR[row.type || row.transaction_type] || 'default'} /></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: Number(row.amount) >= 0 ? 'success.main' : 'error.main' }}>
                      {Number(row.amount) >= 0 ? '+' : ''}{Number(row.amount || 0).toFixed(4)}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.notes || row.description || '—'}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</TableCell>
                  </TableRow>
                ))}
                {txns.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>No transactions found. Use "Add Balance" to top up a customer account.</TableCell></TableRow>}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} rowsPerPage={50}
              onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[50]} />
          </>
        )}
      </Card>

      <Dialog open={topupDialog} onClose={() => setTopupDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust Customer Balance</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth label="Customer *" value={topupForm.tenant_id}
                onChange={e => setTopupForm({ ...topupForm, tenant_id: e.target.value })}>
                <MenuItem value=""><em>Select customer…</em></MenuItem>
                {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Type" value={topupForm.type}
                onChange={e => setTopupForm({ ...topupForm, type: e.target.value })}>
                <MenuItem value="credit">Credit (Add)</MenuItem>
                <MenuItem value="debit">Debit (Deduct)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Amount *" value={topupForm.amount}
                onChange={e => setTopupForm({ ...topupForm, amount: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ step: '0.01', min: '0' }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" value={topupForm.notes}
                onChange={e => setTopupForm({ ...topupForm, notes: e.target.value })}
                placeholder="Reason for adjustment (optional)" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTopupDialog(false)}>Cancel</Button>
          <Button variant="contained" color={topupForm.type === 'debit' ? 'error' : 'success'}
            onClick={doTopup} disabled={saving || !topupForm.tenant_id || !topupForm.amount}>
            {saving ? <CircularProgress size={20} /> : topupForm.type === 'debit' ? 'Deduct Balance' : 'Add Balance'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={billNowDialog} onClose={() => { setBillNowDialog(false); if (billNowResult) load(); }} maxWidth="xs" fullWidth>
        <DialogTitle>Bill Now — Create Draft Invoice</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {billNowErr && <Alert severity="error" sx={{ mb: 2 }}>{billNowErr}</Alert>}
          {!billNowResult ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Creates a <b>draft</b> invoice for usage since the customer's last invoice up to now.
                  If their last invoice is <b>unpaid</b>, it is combined into this one and the old invoice is superseded —
                  so there is a single running balance.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField select fullWidth label="Customer *" value={billNowForm.tenant_id}
                  onChange={e => setBillNowForm({ ...billNowForm, tenant_id: e.target.value })}>
                  <MenuItem value=""><em>Select customer…</em></MenuItem>
                  {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth type="number" label="Due in (days)" value={billNowForm.due_days}
                  onChange={e => setBillNowForm({ ...billNowForm, due_days: e.target.value })}
                  inputProps={{ min: '0' }} />
              </Grid>
            </Grid>
          ) : (
            <Alert severity="success">
              Draft invoice <b>{billNowResult.invoice_number}</b> created — total{' '}
              <b>{Number(billNowResult.total || 0).toFixed(2)} {billNowResult.currency}</b>.<br />
              New usage: {Number(billNowResult.new_usage || 0).toFixed(2)} ({billNowResult.calls} calls, {billNowResult.minutes} min).
              {billNowResult.combined_from
                ? <><br />Combined with unpaid <b>{billNowResult.combined_from}</b> (carried {Number(billNowResult.carried_forward || 0).toFixed(2)}), which was superseded.</>
                : null}
              <br />Review &amp; send it from the Invoices page.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setBillNowDialog(false); if (billNowResult) load(); }}>{billNowResult ? 'Close' : 'Cancel'}</Button>
          {!billNowResult && <Button variant="contained" onClick={doBillNow} disabled={billNowSaving || !billNowForm.tenant_id}>
            {billNowSaving ? <CircularProgress size={20} /> : 'Create Draft'}
          </Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
